/**
 * Phase 14 demo — offline maps, after Apple Maps.
 *
 * The download button (top right) lists downloaded maps and picks new ones:
 * drag the frame's corners or move the map beneath it, check the estimated
 * size, and download. Then turn on "Only Use Offline Maps", or switch the
 * browser offline, and the downloaded area keeps drawing — and search and
 * directions keep working from the downloaded tiles. Reload with the network
 * off and the page itself still opens, from its service worker (`sw.ts`).
 *
 * `?theme=dark`, `?select` to open the area picker straight away.
 */

import { control, Map, OfflineMaps, offlineMaps, setOfflineMaps } from '../../packages/ts-maps/src/core-map'
import { loadBasemap, TILEJSON_SOURCES } from './basemap'

// The page and its downloads, ready for a reload with the network off: the
// service worker keeps the page, and downloads carry on in the background,
// resume when the connection returns, and are kept up to date.
setOfflineMaps(new OfflineMaps({ background: true, autoResume: true }))
if ('serviceWorker' in navigator)
  navigator.serviceWorker.register('./sw.js').catch(() => {})

const params = new URLSearchParams(location.search)
const prefersDark = typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
const theme = (params.get('theme') as 'light' | 'dark' | null) ?? (prefersDark ? 'dark' : 'light')

const map = new Map('map', { center: [37.7793, -122.4193], zoom: 13, theme, zoomControl: false })
control.navigation().addTo(map)
// The TileJSON is kept with each download, so the map can be rebuilt from it
// with no connection.
const offline = control.offlineMaps({ resources: TILEJSON_SOURCES }).addTo(map)

void loadBasemap(map, theme).then(() => {
  if (params.has('select'))
    setTimeout(() => offline.selectArea(), 1500)
})

const scope = globalThis as unknown as { demo: unknown }
scope.demo = { map, offline, maps: offlineMaps() }
